import { flattie } from 'flattie';
import { CountMinSketch, JsBloom } from '@getkoala/js-bloom';
export * from '@getkoala/js-bloom';

const removeIndexes = key => {
  const replaced = key.replace(/\.(\d+)/g, '.[]');
  return replaced;
};

function isEvent(e) {
  if (typeof e !== 'object') {
    return false;
  }

  if (e != null && e.hasOwnProperty('event')) {
    return true;
  }

  return false;
}

function isPageView(e) {
  if (typeof e !== 'object') {
    return false;
  }

  if (e != null && e.hasOwnProperty('page')) {
    if (e.page['path']) {
      return true;
    }
  }

  return false;
}

function increment(profile, key, by) {
  if (by === void 0) {
    by = 1;
  }

  profile.counts.add(key.toLowerCase(), by);
}

function setCount(profile, key, to) {
  profile.counts.setCount(key.toLowerCase(), to);
}

function add(profile, key) {
  profile.bloom.add(key.toLowerCase());
}

function indexPair(profile, key, value) {
  const cleanKey = removeIndexes(key);

  if (typeof value === 'number') {
    // overrides the counts in the Count Min Sketch,
    // as opposed to incrementing the count.
    // This is what allows us to query traits that are numbers
    setCount(profile, cleanKey + ":" + value, value);
    setCount(profile, cleanKey, value);
  } else {
    add(profile, cleanKey + ":" + value);
    add(profile, cleanKey);
  }

  return profile;
}

function index(profile, element) {
  if (Array.isArray(element)) {
    element.forEach(e => {
      index(profile, e);
    });
    return;
  }

  if (isEvent(element)) {
    var _element$properties;

    increment(profile, "events." + element.event);
    const props = Object.entries(flattie((_element$properties = element.properties) != null ? _element$properties : {}));
    props.forEach(_ref => {
      let [key, value] = _ref;
      indexPair(profile, key, value);
    });
    return;
  }

  if (isPageView(element)) {
    increment(profile, "page_views." + element.page.path);
    return;
  }

  if (typeof element === 'object') {
    const flat = Object.entries(flattie(element));
    flat.forEach(_ref2 => {
      let [key, value] = _ref2;
      indexPair(profile, key, value);
    });
    return;
  }

  return;
}

function anonymize(raw) {
  var _raw$events, _raw$pageViews, _raw$traits;

  const counts = new CountMinSketch();
  const bloom = new JsBloom({
    size: 10000
  });
  const profile = {
    counts,
    bloom
  };
  (_raw$events = raw.events) == null ? void 0 : _raw$events.forEach(e => {
    index(profile, e);
  });
  (_raw$pageViews = raw.pageViews) == null ? void 0 : _raw$pageViews.map(view => {
    index(profile, view);
  });
  index(profile, (_raw$traits = raw.traits) != null ? _raw$traits : {});
  index(profile, {
    account_score: raw.accountScore
  });
  index(profile, {
    company: raw.firmographics
  });
  index(profile, {
    person: raw.person
  });
  return profile;
}

function stringMatcher(traits, key) {
  return {
    is: target => traits.is(key, target),
    exists: () => traits.has(key),
    includesItem: function () {
      for (var _len = arguments.length, targets = new Array(_len), _key = 0; _key < _len; _key++) {
        targets[_key] = arguments[_key];
      }

      return traits.includes(key, targets);
    },
    includesAnyOf: function () {
      for (var _len2 = arguments.length, targets = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
        targets[_key2] = arguments[_key2];
      }

      return traits.includesAnyOf(key, targets);
    },
    not: {
      is: target => !traits.is(key, target),
      includesItem: function () {
        for (var _len3 = arguments.length, targets = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
          targets[_key3] = arguments[_key3];
        }

        return !traits.includes(key, targets);
      },
      includesAnyOf: function () {
        for (var _len4 = arguments.length, targets = new Array(_len4), _key4 = 0; _key4 < _len4; _key4++) {
          targets[_key4] = arguments[_key4];
        }

        return !traits.includesAnyOf(key, targets);
      }
    }
  };
}
function numberMatcher(traits, key) {
  return {
    exists: () => traits.has(key),
    is: target => traits.is(key, target),
    greaterThan: target => traits.greaterThan(key, target),
    greaterThanOrEqual: target => traits.greaterThanOrEqual(key, target),
    lessThan: target => traits.lessThan(key, target),
    lessThanOrEqual: target => traits.lessThanOrEqual(key, target),
    not: {
      is: target => !traits.is(key, target),
      greaterThan: target => !traits.greaterThan(key, target),
      greaterThanOrEqual: target => !traits.greaterThanOrEqual(key, target),
      lessThan: target => !traits.lessThan(key, target),
      lessThanOrEqual: target => !traits.lessThanOrEqual(key, target)
    }
  };
}
function has(profile, trait) {
  const t = trait.toLowerCase();
  return !!(profile.bloom.test(t) || profile.counts.count(t));
}
function greaterThan(profile, trait, value) {
  return profile.counts.count(trait.toLowerCase()) > value;
}
function greaterThanOrEqual(profile, trait, value) {
  return profile.counts.count(trait.toLowerCase()) >= value;
}
function lessThan(profile, trait, value) {
  return profile.counts.count(trait.toLowerCase()) < value;
}
function lessThanOrEqual(profile, trait, value) {
  return profile.counts.count(trait.toLowerCase()) <= value;
}
function is(profile, trait, value) {
  if (value === undefined) {
    return has(profile, trait);
  }

  if (typeof value === 'object') {
    return matchesObject(profile, trait, value);
  }

  if (typeof value === 'number') {
    return profile.counts.count(trait.toLowerCase()) === value;
  }

  return profile.bloom.test((trait + ":" + value).toLowerCase());
}
function matchesObject(profile, trait, value) {
  const flat = flattie(value);
  const allKeys = Object.keys(flat);
  return allKeys.every(key => is(profile, trait + "." + key, value[key]));
}
function includes(profile, trait, value) {
  return is(profile, trait, value) || is(profile, trait + ".[]", value);
}
function includesAnyOf(profile, trait, values) {
  return values.some(value => includes(profile, trait, value));
}
function includesAllOf(profile, trait, values) {
  return values.every(value => includes(profile, trait, value));
}
class Traits {
  constructor(profile) {
    this.profile = void 0;
    this.profile = profile;
    this.profile = profile;
  }

  has(trait) {
    return has(this.profile, trait);
  }

  greaterThan(trait, value) {
    return greaterThan(this.profile, trait, value);
  }

  greaterThanOrEqual(trait, value) {
    return greaterThanOrEqual(this.profile, trait, value);
  }

  lessThan(trait, value) {
    return lessThan(this.profile, trait, value);
  }

  lessThanOrEqual(trait, value) {
    return lessThanOrEqual(this.profile, trait, value);
  }

  is(trait, value) {
    return is(this.profile, trait, value);
  }

  matchesObject(trait, value) {
    return matchesObject(this.profile, trait, value);
  }

  includes(trait, value) {
    return includes(this.profile, trait, value);
  }

  includesAnyOf(trait, values) {
    return includesAnyOf(this.profile, trait, values);
  }

  includesAllOf(trait, values) {
    return includesAllOf(this.profile, trait, values);
  }

  get not() {
    return {
      has: trait => !this.has(trait),
      greaterThan: (trait, value) => !this.greaterThan(trait, value),
      greaterThanOrEqual: (trait, value) => !this.greaterThanOrEqual(trait, value),
      lessThan: (trait, value) => !this.lessThan(trait, value),
      lessThanOrEqual: (trait, value) => !this.lessThanOrEqual(trait, value),
      is: (trait, value) => !this.is(trait, value),
      matchesObject: (trait, value) => !this.matchesObject(trait, value),
      includes: (trait, value) => !this.includes(trait, value),
      includesAnyOf: (trait, values) => !this.includesAnyOf(trait, values),
      includesAllOf: (trait, values) => !this.includesAllOf(trait, values)
    };
  }

}

function stringMatcher$1(traits, key) {
  return {
    exists: () => traits.has(key),
    is: target => traits.is(key, target),
    includesItem: function () {
      for (var _len = arguments.length, targets = new Array(_len), _key = 0; _key < _len; _key++) {
        targets[_key] = arguments[_key];
      }

      return traits.includes(key, targets);
    },
    includesAnyOf: function () {
      for (var _len2 = arguments.length, targets = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
        targets[_key2] = arguments[_key2];
      }

      return traits.includesAnyOf(key, targets);
    }
  };
}

function arrayMatcher(traits, key) {
  return {
    includesItem: target => traits.includes(key, target),
    includesAnyOf: function () {
      for (var _len3 = arguments.length, targets = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
        targets[_key3] = arguments[_key3];
      }

      return traits.includesAnyOf(key, targets);
    },
    includesAllOf: function () {
      for (var _len4 = arguments.length, targets = new Array(_len4), _key4 = 0; _key4 < _len4; _key4++) {
        targets[_key4] = arguments[_key4];
      }

      return traits.includesAllOf(key, targets);
    }
  };
}

function numberMatcher$1(traits, key) {
  return {
    exists: () => traits.has(key),
    is: target => traits.is(key, target),
    greaterThan: target => traits.greaterThan(key, target),
    greaterThanOrEqual: target => traits.greaterThanOrEqual(key, target),
    lessThan: target => traits.lessThan(key, target),
    lessThanOrEqual: target => traits.lessThanOrEqual(key, target)
  };
}

class Company {
  constructor(profile) {
    this.traits = void 0;
    this.traits = new Traits(profile);
  }

  get name() {
    return stringMatcher$1(this.traits, 'company.name');
  }

  get domain() {
    return stringMatcher$1(this.traits, 'company.domain');
  }

  get sector() {
    return stringMatcher$1(this.traits, 'company.category.sector');
  }

  get type() {
    return stringMatcher$1(this.traits, 'company.type');
  }

  get industryGroup() {
    return stringMatcher$1(this.traits, 'company.category.industryGroup');
  }

  get industry() {
    return stringMatcher$1(this.traits, 'company.category.industry');
  }

  get subIndustry() {
    return stringMatcher$1(this.traits, 'company.category.subIndustry');
  }

  get foundedYear() {
    return numberMatcher$1(this.traits, 'company.foundedYear');
  }

  get timezone() {
    return stringMatcher$1(this.traits, 'company.timezone');
  }

  get city() {
    return stringMatcher$1(this.traits, 'company.geo.city');
  }

  get state() {
    return stringMatcher$1(this.traits, 'company.geo.state');
  }

  get stateCode() {
    return stringMatcher$1(this.traits, 'company.geo.stateCode');
  }

  get country() {
    return stringMatcher$1(this.traits, 'company.geo.country');
  }

  get countryCode() {
    return stringMatcher$1(this.traits, 'company.geo.countryCode');
  }

  get employeeCount() {
    return numberMatcher$1(this.traits, 'company.metrics.employees');
  }

  get employeesRange() {
    return stringMatcher$1(this.traits, 'company.metrics.employeesRange');
  }

  get marketCap() {
    return numberMatcher$1(this.traits, 'company.metrics.marketCap');
  }

  get amountRaised() {
    return numberMatcher$1(this.traits, 'company.metrics.raised');
  }

  get annualRevenue() {
    return numberMatcher$1(this.traits, 'company.metrics.annualRevenue');
  }

  get estimatedAnnualRevenue() {
    return stringMatcher$1(this.traits, 'company.metrics.estimatedAnnualRevenue');
  }

  get tech() {
    return arrayMatcher(this.traits, 'company.tech');
  }

  get techCategories() {
    return arrayMatcher(this.traits, 'company.techCategories');
  }

  get tags() {
    return arrayMatcher(this.traits, 'company.tags');
  }

  isB2B() {
    return this.tags.includesItem('B2B');
  }

  isB2C() {
    return this.tags.includesItem('B2C');
  }

  isEnterprise() {
    return this.tags.includesItem('Enterprise');
  }

  isEcommerce() {
    return this.tags.includesItem('E-commerce');
  }

  isSaas() {
    return this.tags.includesItem('SAAS');
  }

}

function performed(profile, event, times) {
  if (times === void 0) {
    times = 1;
  }

  return performedAtLeast(profile, times, event);
}
function performedAnyOf(profile) {
  for (var _len = arguments.length, events = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    events[_key - 1] = arguments[_key];
  }

  return events.some(event => performed(profile, event));
}
function performedAtLeast(profile, times, event) {
  return profile.counts.count(("events." + event).toLowerCase()) >= times;
}
class Events {
  constructor(profile) {
    this.profile = void 0;
    this.profile = profile;
  }

  performed(event, times) {
    if (times === void 0) {
      times = 1;
    }

    return performed(this.profile, event, times);
  }

  performedAnyOf() {
    for (var _len2 = arguments.length, events = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      events[_key2] = arguments[_key2];
    }

    return performedAnyOf(this.profile, ...events);
  }

  performedAtLeast(times, event) {
    return performedAtLeast(this.profile, times, event);
  }

  get not() {
    var _this = this;

    return {
      performed: function (event, times) {
        if (times === void 0) {
          times = 1;
        }

        return !_this.performed(event, times);
      },
      performedAnyOf: function () {
        return !_this.performedAnyOf(...arguments);
      },
      performedAtLeast: (times, event) => {
        return !this.performedAtLeast(times, event);
      }
    };
  }

}

function inSegment() {
  for (var _len = arguments.length, predicates = new Array(_len), _key = 0; _key < _len; _key++) {
    predicates[_key] = arguments[_key];
  }

  return predicates.every(Boolean);
}
async function when(condition, action, timeout) {
  return new Promise(async (resolve, _reject) => {
    if (await condition()) {
      action == null ? void 0 : action();
      resolve();
      return;
    }

    const check = () => setTimeout(async () => {
      if (await condition()) {
        action == null ? void 0 : action();
        resolve();
      } else {
        check();
      }
    }, timeout);

    check();
  });
}

// TODO:
// - add UTM matchers
// - add query params matchers
// - add referrer matchers
// - add traffic source matchers
// - add user agent matchers
// - turn this into a full browser API
function seen(profile, path, times) {
  if (times === void 0) {
    times = 1;
  }

  return profile.counts.count(("page_views." + path).toLowerCase()) >= times;
}
const viewed = seen;
function viewing(_profile, path) {
  return window.location.pathname.toLowerCase() === path.toLowerCase();
}
class Page {
  constructor(profile) {
    this.profile = void 0;
    this.profile = profile;
    this.profile = profile;
  }

  seen(path, times) {
    if (times === void 0) {
      times = 1;
    }

    return seen(this.profile, path, times);
  }

  viewed(path) {
    return viewed(this.profile, path);
  }

  viewing(path) {
    return viewing(this.profile, path);
  }

  get not() {
    var _this = this;

    return {
      seen: function (path, times) {
        if (times === void 0) {
          times = 1;
        }

        return !_this.seen(path, times);
      },
      viewed: path => {
        return !this.viewed(path);
      },
      viewing: path => {
        return !this.viewing(path);
      }
    };
  }

}

class Person {
  constructor(profile) {
    this.traits = void 0;
    this.traits = new Traits(profile);
  }

  get emailType() {
    return stringMatcher(this.traits, 'person.email_type');
  }

  get identifiedWorkEmail() {
    return this.emailType.is('work');
  }

  get identifiedPersonalEmail() {
    return this.emailType.is('personal');
  }

  get identifiedAcademicEmail() {
    return this.emailType.is('academic');
  }

  get isIdentified() {
    return this.traits.has('person.email_type');
  }

  get timezone() {
    return stringMatcher(this.traits, 'person.timezone');
  }

  get city() {
    return stringMatcher(this.traits, 'person.geo.city');
  }

  get state() {
    return stringMatcher(this.traits, 'person.geo.state');
  }

  get stateCode() {
    return stringMatcher(this.traits, 'person.geo.stateCode');
  }

  get country() {
    return stringMatcher(this.traits, 'person.geo.country');
  }

  get countryCode() {
    return stringMatcher(this.traits, 'person.geo.countryCode');
  }

  get company() {
    return stringMatcher(this.traits, 'person.employment.name');
  }

  get title() {
    return stringMatcher(this.traits, 'person.employment.title');
  }

  get role() {
    return stringMatcher(this.traits, 'person.employment.role');
  }

  get subRole() {
    return stringMatcher(this.traits, 'person.employment.subRole');
  }

  get seniority() {
    return stringMatcher(this.traits, 'person.employment.seniority');
  }

}

class Scores {
  constructor(profile, prefix) {
    this.traits = void 0;
    this.prefix = '';
    this.traits = new Traits(profile);
    this.prefix = prefix;
  }

  get fitGrade() {
    return numberMatcher(this.traits, this.prefix + ".fit_grade");
  }

  get fitGradeLetter() {
    return stringMatcher(this.traits, this.prefix + ".fit_grade_letter");
  }

}

function index$1(profile, indexable) {
  index(profile, indexable);
}

function build(data) {
  const bloom = new JsBloom(data.b);
  const counts = new CountMinSketch(data.c);
  const profile = {
    counts,
    bloom
  };
  return {
    traits: new Traits(profile),
    events: new Events(profile),
    page: new Page(profile),
    company: new Company(profile),
    person: new Person(profile),
    scores: {
      account: new Scores(profile, 'account_score')
    },
    inSegment,
    when,
    raw: profile,
    index: indexable => index$1(profile, indexable)
  };
} // Useful for testing

function anonymize$1(raw) {
  return anonymize(raw);
}

export { anonymize$1 as anonymize, build };
//# sourceMappingURL=edge-api-client.esm.js.map
